import {
  BannerWrapper,
  Card,
  Typography,
  Button,
  DialogPosition,
  ActionModal,
} from '@ecdlink/ui';
import { useHistory, useLocation } from 'react-router-dom';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useAppDispatch } from '@store';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import ROUTES from '@routes/routes';
import {
  InfantRegisterSteps,
  MultipleChildrenProps,
} from './infant-register-form.types';
import { ConsentAgreement } from '../components/consent-agrement/consent-agreement';
import {
  MotherDetails,
  MOTHER_TYPE_ID,
} from '../components/mother-details/mother-details';
import { MotherContactInformation } from '../components/mother-contact-information/mother-contact-information';
import { InfantAddress } from '../components/infant-address/infant-address';
import { InfantRoadToHealth } from '../components/infant-road-to-health/infant-road-to-health';
import { InfantDetails } from '../components/infantDetails/infant-details';
import { InfantDetailsModel } from '@/schemas/infant/infant-details';
import { InfantRoadToHealthModel } from '@/schemas/infant/infant-road-to-health';
import { MothertContactInformationModel } from '@/schemas/infant/mother-contact-information';
import { newGuid } from '@utils/common/uuid.utils';
import {
  Document,
  CaregiverDto,
  InfantDto,
  SiteAddressDto,
  UserDto,
  useDialog,
  getPreviousAndNextMonths,
  MotherDto,
  VisitDto,
} from '@ecdlink/core/lib';
import { EditConsentAgreementProps } from '../components/consent-agrement/consent-agreement.types';
import { infantActions, infantThunkActions } from '@/store/infant';
import momImage from '@/assets/happyMom.svg';
import { useSelector } from 'react-redux';
import { userSelectors } from '@store/user';
import {
  caregiverActions,
  caregiverSelectors,
  caregiverThunkActions,
} from '@/store/caregiver';
import { MotherDetailsProps } from '../components/mother-details/mother-details.types';
import { useStaticData } from '@/hooks/useStaticData';
import { FileTypeEnum, WorkflowStatusEnum } from '@ecdlink/graphql';
import { documentActions, documentThunkActions } from '@/store/document';
import { useWindowSize } from '@reach/window-size';
import { MotherDetailsModel } from '@/schemas/infant/mother-details';
import { useThunkFetchCall } from '@/hooks/useThunkFetchCall';
import { usePrevious } from '@ecdlink/core/lib/hooks/usePrevious';
import { healthCareWorkerSelectors } from '@/store/healthCareWorker';
import { InfantActions } from '@/store/infant/infant.actions';
import { InfantRouteState } from '../infant.types';
import { motherActions, motherSelectors } from '@/store/mother';
import { RootState } from '@/store/types';
import { eventRecordThunkActions } from '@/store/eventRecord';
import { EventRecordActions } from '@/store/eventRecord/eventRecord.actions';
import { useRequestResponseDialog } from '@/hooks/useRequestResponseDialog';
import { PregnantProfileRouteState } from '@/pages/mom/pregnant-profile/index.types';
import { Message } from '@models/messages/messages';
import { NotificationPriority } from '../../../services/NotificationService/NotificationService.types';
import { notificationActions } from '@/store/notifications';
import {
  getAllInfantFirstActiveChildEventSelector,
  getInfantFirstActiveChildEventSelector,
} from '@/store/infant/infant.selectors';
import { staticDataSelectors } from '@/store/static-data';
import { getInfantStatusInfo } from '@/utils/infant/infantVisitStatus.utils';
import { HcwTabIndex } from '@/pages/client/client-dashboard/class-dashboard.types';
import { useHighlights } from '@/hooks/useHighlights';

const BANNER_HEIGHT = 64;

interface onSubmit {
  caregiverDetails?: MotherDetailsModel;
  caregiverAddress?: string;
}

// Helper function to check for duplicate children
const isDuplicateChild = (
  child: MultipleChildrenProps,
  existingChildren: MultipleChildrenProps[]
): boolean => {
  return existingChildren.some(
    (existing) =>
      existing.firstName?.toLowerCase() === child.firstName?.toLowerCase() &&
      existing.dateOfBirth?.getTime() === child.dateOfBirth?.getTime() &&
      existing.genderId === child.genderId
  );
};

// Helper function to create a unique key for a child
const getChildKey = (child: MultipleChildrenProps): string => {
  return `${child.firstName}-${child.dateOfBirth?.getTime()}-${child.genderId}`;
};

export const InfantRegisterForm: React.FC = () => {
  const [label, setLabel] = useState('');
  const { isOnline } = useOnlineStatus();
  const appDispatch = useAppDispatch();
  const history = useHistory();
  const user = useSelector(userSelectors.getUser);
  const [hasConsent, setHasConsent] = useState<EditConsentAgreementProps>();
  const [details, setDetails] = useState<MotherDetailsProps>();
  const [infantDetails, setInfantDetails] = useState<InfantDetailsModel>();
  const [infantVisits, setInfantVisits] = useState<VisitDto[]>();
  const [infantRoadToHealthBook, setInfantRoadToHealthBook] =
    useState<InfantRoadToHealthModel>();
  const [contactInformation, setContactInformation] =
    useState<MothertContactInformationModel>();
  const [address, setAddress] = useState<string>();
  const [isAlreadyClient, setIsAlreadyClient] = useState<any>(null);
  const [multipleChildren, setMultipleChildren] = useState<
    boolean | undefined
  >();
  const [activeStep, setActiveStep] = useState(
    InfantRegisterSteps.consentAgreement
  );
  const [multipleChildrenArray, setMultipleChildrenArray] = useState<
    MultipleChildrenProps[]
  >([]);

  // Use ref to track processed children to avoid race conditions
  const processedChildrenRef = useRef<Set<string>>(new Set());

  // Track the current child being edited for preserving form state
  const [currentChildIndex, setCurrentChildIndex] = useState<number>(0);

  let numberOfChildren: number | undefined = hasConsent?.numberOfChildren;
  const [multipleChildrenCount, setMultipleChildrenCount] = useState<number>(1);
  const [relationshipId, setRelationshipId] = useState<string | undefined>();
  const relations = useSelector(staticDataSelectors.getRelations);
  const motherRelationshipType = relations.find(
    (item) => item.description === 'Mother'
  );

  const [firstChild] = multipleChildrenArray;

  const { height } = useWindowSize();

  const location = useLocation<InfantRouteState & PregnantProfileRouteState>();
  const motherId = location.state?.motherId;
  const infantId = location.state.linkedInfantId;
  const isInfantEvent = !!location.state.isInfantEvent;
  const bornEventId = location.state?.bornEventId;
  const notes = location.state?.notes;

  const mother = useSelector((state: RootState) => {
    const submittedDetails = details as unknown as MotherDetailsModel;
    const lookupId =
      motherId ||
      firstChild?.caregiver?.id ||
      (isAlreadyClient && submittedDetails?.id
        ? submittedDetails.id
        : undefined);

    if (lookupId) {
      return motherSelectors.getMotherById(state, lookupId) ?? {};
    }

    return {};
  });
  const caregiver = useSelector(
    caregiverSelectors.getCaregiverById(motherId || '')
  );

  const { totalInfantsForMonth } = useHighlights();

  const dialog = useDialog();

  const { getWorkflowStatusIdByEnum, getDocumentTypeIdByEnum } =
    useStaticData();

  const healthCareWorker = useSelector(
    healthCareWorkerSelectors.getHealthCareWorker
  );

  const registerChildEvent = useSelector((state: RootState) =>
    getAllInfantFirstActiveChildEventSelector(state)
  );

  const { isLoading, isFulfilled } = useThunkFetchCall(
    'infants',
    InfantActions.ADD_INFANTS
  );

  const { isLoading: isLoadingEventRecord, isRejected: isRejectedEventRecord } =
    useThunkFetchCall('eventRecord', EventRecordActions.ADD_EVENT_RECORD);

  const wasLoading = usePrevious(isLoading);
  const wasLoadingEventRecord = usePrevious(isLoadingEventRecord);

  const { errorDialog } = useRequestResponseDialog();

  const caregiverInfoFromRecordEvent = useMemo(() => {
    if (isInfantEvent) {
      return caregiver || mother;
    }

    if (motherId) {
      return mother;
    }

    return undefined;
  }, [caregiver, isInfantEvent, mother, motherId]) as
    | (MotherDto & CaregiverDto)
    | undefined;

  const successMessage = useMemo(
    () =>
      multipleChildrenArray.length > 1
        ? multipleChildrenArray.length + ' children.'
        : '1 child.',
    [multipleChildrenArray.length]
  );

  useEffect(() => {
    setLabel('step 1 of 6');
  }, []);

  const isMotherCaregiver = useMemo(
    () =>
      !!caregiverInfoFromRecordEvent?.user ||
      !!caregiverInfoFromRecordEvent?.id,
    [caregiverInfoFromRecordEvent]
  );

  // OPTIMIZED: Extracted infant creation logic to reduce code duplication
  const createInfantDto = useCallback(
    (
      child: Partial<MultipleChildrenProps>,
      caregiverInput: CaregiverDto,
      childUserId: string
    ): InfantDto => {
      let weightAtBirth: number | undefined;
      let lengthAtBirth: number | undefined;

      if (child?.roadToHealthBook) {
        // Convert grams to kg
        weightAtBirth = child.weightAtBirth
          ? Math.trunc(
              (Number(String(child.weightAtBirth).replace(',', '.')) / 1000) *
                10
            ) / 10
          : undefined;

        lengthAtBirth = child.lengthAtBirth
          ? Number(String(child.lengthAtBirth).replace(',', '.'))
          : undefined;
      }

      const userChild: UserDto = {
        id: childUserId,
        firstName: child.firstName || '',
        dateOfBirth: child.dateOfBirth?.toISOString(),
      };

      const updatedVisits: VisitDto[] =
        child.visits?.map((visit) => ({
          ...visit,
          clientId: childUserId,
        })) ?? [];

      return {
        id: childUserId,
        dateOfBirth: child.dateOfBirth?.toISOString(),
        firstName: child.firstName,
        genderId: child.genderId ?? '',
        documents: child.roadToHealthBook,
        userId: childUserId,
        user: userChild,
        weightAtBirth,
        lengthAtBirth,
        caregiverId: caregiverInput.id,
        caregiver: caregiverInput,
        synced: isOnline,
        isNew: true,
        visits: updatedVisits,
        statusInfo: getInfantStatusInfo(updatedVisits),
        isActive: true,
        insertedDate: new Date().toISOString(),
      };
    },
    [isOnline]
  );

  // OPTIMIZED: Extracted document creation logic
  const createDocumentDto = useCallback(
    (roadToHealthBook: string, childUserId: string): Document => {
      const fileName = 'roadtohealthbook.png';
      const workflowStatusId = getWorkflowStatusIdByEnum(
        WorkflowStatusEnum.DocumentPendingVerification
      );
      const documentTypeId = getDocumentTypeIdByEnum(
        FileTypeEnum.RoadToHealthBook
      );

      return {
        id: newGuid(),
        userId: childUserId,
        createdUserId: healthCareWorker?.userId ?? user?.id,
        workflowStatusId: workflowStatusId ?? '',
        documentTypeId: documentTypeId ?? '',
        name: fileName,
        fileName: fileName,
        file: roadToHealthBook,
        fileType: FileTypeEnum.RoadToHealthBook,
        synced: isOnline,
      };
    },
    [
      getDocumentTypeIdByEnum,
      getWorkflowStatusIdByEnum,
      healthCareWorker?.userId,
      isOnline,
      user?.id,
    ]
  );

  // OPTIMIZED: Process infant with optional document
  const processInfant = useCallback(
    async (
      child: Partial<MultipleChildrenProps>,
      caregiverInput: CaregiverDto
    ) => {
      const childUserId = newGuid();
      const infantInputModel = createInfantDto(
        child,
        caregiverInput,
        childUserId
      );

      appDispatch(infantActions.addInfant(infantInputModel));

      if (isOnline) {
        await appDispatch(
          infantThunkActions.addInfant({
            infant: infantInputModel,
            motherId,
          })
        ).unwrap();
      }

      // Handle Road to Health Book document
      if (child.roadToHealthBook) {
        const documentInputModel = createDocumentDto(
          child.roadToHealthBook,
          childUserId
        );

        appDispatch(documentActions.createDocument(documentInputModel));

        if (isOnline) {
          await appDispatch(
            documentThunkActions.createDocument(documentInputModel)
          ).unwrap();
        }
      } else if (!child.roadToHealthBook && child.firstName) {
        // Create notification for missing document
        const notification: Message = {
          reference: `${childUserId}-docs`,
          title: `Upload ${child.firstName}'s Road to Health Book`,
          message: `Add a picture of page ii of ${child.firstName}'s Road to Health Book. Refer ${caregiverInput.firstName} to the clinic if they do not have ${child.firstName}'s document.`,
          dateCreated: new Date().toISOString(),
          priority: NotificationPriority.higher,
          viewOnDashboard: true,
          area: 'child-registration',
          icon: 'IdentificationIcon',
          color: 'primary',
          actionText: 'Upload image of RTH',
          viewType: 'Both',
          cta: 'RoadToHealth',
          routeConfig: {
            route: `${ROUTES.CLIENTS.INFANT_PROFILE.ROOT}${childUserId}`,
            params: {
              isRoadToHealthBook: true,
            },
          },
        };
        appDispatch(notificationActions.addNotification(notification));
      }
    },
    [appDispatch, createDocumentDto, createInfantDto, isOnline, motherId]
  );

  const completeAllSteps = useCallback(
    async ({ caregiverDetails, caregiverAddress }: onSubmit) => {
      const newCaregiverId = newGuid();
      const siteAddressId = newGuid();

      const siteAddress: SiteAddressDto = {
        id: siteAddressId,
        addressLine1: address || caregiverAddress || '',
      };

      const caregiverInput: CaregiverDto = {
        id:
          firstChild?.caregiver?.id ||
          caregiverDetails?.id ||
          details?.id ||
          newCaregiverId,
        firstName: caregiverDetails?.name || details?.name || '',
        surname: caregiverDetails?.surname || details?.surname || '',
        phoneNumber: contactInformation?.cellphone ?? '',
        whatsAppNumber:
          contactInformation?.whatsapp || contactInformation?.cellphone,
        age: caregiverDetails?.age || details?.age || '',
        siteAddress: siteAddress,
        isActive: true,
        isMother: isMotherCaregiver,
        insertedDate: new Date().toISOString(),
        relationId: isMotherCaregiver
          ? motherRelationshipType?.id
          : caregiverDetails?.relationshipId ||
            firstChild?.relationshipId ||
            details?.relationshipId ||
            '',
        healthCareWorkerId: healthCareWorker?.id,
        synced: isOnline,
      };

      setRelationshipId(
        isMotherCaregiver
          ? motherRelationshipType?.id
          : caregiverDetails?.relationshipId ||
              firstChild?.relationshipId ||
              details?.relationshipId
      );

      if (!details?.isMother) {
        appDispatch(caregiverActions.createCaregiver(caregiverInput));
      }

      // OPTIMIZED: Process all children using the extracted function
      if (multipleChildrenArray.length >= 1) {
        for (const child of multipleChildrenArray) {
          await processInfant(child, caregiverInput);
        }
      } else if (infantDetails) {
        // Single child case
        const childData = {
          ...infantDetails,
          ...infantRoadToHealthBook,
          visits: infantVisits,
        };
        await processInfant(childData, caregiverInput);
      }

      if (!isOnline) {
        // redirect when offline
        history.push(ROUTES.CLIENTS.ROOT, {
          activeTabIndex: HcwTabIndex.CLIENT,
        });
      }
    },
    [
      address,
      appDispatch,
      contactInformation?.cellphone,
      contactInformation?.whatsapp,
      details?.age,
      details?.id,
      details?.isMother,
      details?.name,
      details?.relationshipId,
      details?.surname,
      firstChild?.caregiver?.id,
      firstChild?.relationshipId,
      healthCareWorker?.id,
      infantDetails,
      infantRoadToHealthBook,
      infantVisits,
      isMotherCaregiver,
      isOnline,
      motherRelationshipType?.id,
      multipleChildrenArray,
      processInfant,
      history,
    ]
  );

  const handleExistingUser = useCallback(
    ({ caregiverDetails }: onSubmit) => {
      if (isAlreadyClient) {
        completeAllSteps({ caregiverDetails });
        return;
      }
      setActiveStep(InfantRegisterSteps.pregnantContactInformation);
    },
    [completeAllSteps, isAlreadyClient]
  );

  const handleMultipleChildrenSteps = useCallback(() => {
    if (multipleChildrenCount < Number(numberOfChildren)) {
      // Reset form values for next child
      setInfantDetails(undefined);
      setInfantRoadToHealthBook(undefined);
      setInfantVisits(undefined);
      setCurrentChildIndex(multipleChildrenCount); // Move to next child index
      setActiveStep(InfantRegisterSteps.infantDetails);
      setMultipleChildrenCount(multipleChildrenCount + 1);
      setLabel(`step 2 of 6`);
      return;
    }
    setLabel(`step 4 of 6`);
    setActiveStep(InfantRegisterSteps.motherDetails);
  }, [multipleChildrenCount, numberOfChildren]);

  const steps = useCallback(
    (step: InfantRegisterSteps) => {
      switch (step) {
        case InfantRegisterSteps.infantDetails:
          // Load existing data if navigating back to a previously completed child
          const existingChild = multipleChildrenArray[currentChildIndex];

          return (
            <InfantDetails
              multipleChildrenCount={multipleChildrenCount}
              numberOfChildren={numberOfChildren}
              motherInfo={caregiverInfoFromRecordEvent}
              name={infantDetails?.firstName || existingChild?.firstName}
              dateOfBirth={
                infantDetails?.dateOfBirth || existingChild?.dateOfBirth
              }
              genderId={infantDetails?.genderId || existingChild?.genderId}
              setInfantVisits={setInfantVisits}
              onSubmit={(value) => {
                setLabel(`step 3 of 6`);
                setInfantDetails(value);
                setActiveStep(InfantRegisterSteps.infantRoadToHealth);
              }}
            />
          );
        case InfantRegisterSteps.infantRoadToHealth:
          const existingChildRTH = multipleChildrenArray[currentChildIndex];

          return (
            <InfantRoadToHealth
              infantDetails={infantDetails}
              weightAtBirth={
                infantRoadToHealthBook?.weightAtBirth ||
                existingChildRTH?.weightAtBirth
              }
              lengthAtBirth={
                infantRoadToHealthBook?.lengthAtBirth ||
                existingChildRTH?.lengthAtBirth
              }
              roadToHealthBook={
                infantRoadToHealthBook?.roadToHealthBook ||
                existingChildRTH?.roadToHealthBook
              }
              notRoadToHealthBook={
                infantRoadToHealthBook?.notRoadToHealthBook ??
                existingChildRTH?.notRoadToHealthBook
              }
              onSubmit={(value) => {
                setInfantRoadToHealthBook(value);

                // Only add to multipleChildrenArray if there are actually multiple children
                if (numberOfChildren && numberOfChildren > 1) {
                  const newChildRecord: MultipleChildrenProps = {
                    ...infantDetails!,
                    ...value,
                    visits: infantVisits,
                  };
                  const childKey = getChildKey(newChildRecord);

                  setMultipleChildrenArray((prev) => {
                    const updated = [...prev];

                    // Check if we're updating an existing child
                    if (updated[currentChildIndex]) {
                      // Remove old key from ref if it changed
                      const oldChild = updated[currentChildIndex];
                      const oldKey = getChildKey(oldChild);
                      if (oldKey !== childKey) {
                        processedChildrenRef.current.delete(oldKey);
                      }
                      // Update existing child
                      updated[currentChildIndex] = newChildRecord;
                      processedChildrenRef.current.add(childKey);
                    } else {
                      // Add new child (but check for duplicates first)
                      if (
                        !processedChildrenRef.current.has(childKey) &&
                        !isDuplicateChild(newChildRecord, prev)
                      ) {
                        updated.push(newChildRecord);
                        processedChildrenRef.current.add(childKey);
                      }
                    }

                    return updated;
                  });
                }

                handleMultipleChildrenSteps();
              }}
            />
          );
        case InfantRegisterSteps.motherDetails:
          return (
            <MotherDetails
              setMultipleChildrenArray={setMultipleChildrenArray}
              multipleChildrenArray={multipleChildrenArray}
              infantDetails={infantDetails}
              setContactInformation={setContactInformation}
              setAddress={setAddress}
              setIsAlreadyClient={setIsAlreadyClient}
              isAlreadyClient={isAlreadyClient}
              motherInfo={caregiverInfoFromRecordEvent}
              existingDetails={details}
              onSubmit={(value) => {
                setDetails(value as any);
                handleExistingUser({ caregiverDetails: value });

                if (isAlreadyClient) {
                  return setLabel(`step 4 of 4`);
                }

                return setLabel(`step 5 of 6`);
              }}
            />
          );
        case InfantRegisterSteps.pregnantContactInformation:
          return (
            <MotherContactInformation
              details={details}
              existingContactInfo={contactInformation}
              onSubmit={(value) => {
                setLabel(`step 6 of 6`);
                setActiveStep(InfantRegisterSteps.pregnantAddress);
                setContactInformation(value);
              }}
            />
          );
        case InfantRegisterSteps.pregnantAddress:
          return (
            <InfantAddress
              details={details}
              infantDetails={infantDetails}
              address={address}
              onSubmit={(value) => {
                setLabel(`step 6 of 6`);
                setAddress(value.address);
                completeAllSteps({ caregiverAddress: value.address });
              }}
            />
          );
        case InfantRegisterSteps.consentAgreement:
        default:
          return (
            <ConsentAgreement
              multipleChildren={multipleChildren}
              setMultipleChildren={setMultipleChildren}
              hasConsent={hasConsent?.hasConsent}
              numberOfChildren={hasConsent?.numberOfChildren}
              onSubmit={(value) => {
                const previousCount =
                  hasConsent?.numberOfChildren ?? (hasConsent ? 1 : undefined);
                const nextCount = multipleChildren
                  ? value?.numberOfChildren
                  : 1;

                if (
                  previousCount !== undefined &&
                  previousCount !== nextCount
                ) {
                  processedChildrenRef.current.clear();
                  setMultipleChildrenArray([]);
                  setCurrentChildIndex(0);
                  setInfantDetails(undefined);
                  setInfantRoadToHealthBook(undefined);
                  setInfantVisits(undefined);
                  setMultipleChildrenCount(1);
                }

                setActiveStep(InfantRegisterSteps.infantDetails);
                setHasConsent(value as any);
                setLabel(`step 2 of 6`);
              }}
            />
          );
      }
    },
    [
      address,
      caregiverInfoFromRecordEvent,
      completeAllSteps,
      contactInformation,
      currentChildIndex,
      details,
      handleExistingUser,
      handleMultipleChildrenSteps,
      infantDetails,
      hasConsent,
      infantRoadToHealthBook?.lengthAtBirth,
      infantRoadToHealthBook?.notRoadToHealthBook,
      infantRoadToHealthBook?.roadToHealthBook,
      infantRoadToHealthBook?.weightAtBirth,
      infantVisits,
      isAlreadyClient,
      multipleChildren,
      multipleChildrenArray,
      multipleChildrenCount,
      numberOfChildren,
    ]
  );

  const showSuccessMessage = useCallback(
    () =>
      dialog({
        position: DialogPosition.Middle,
        color: 'bg-transparent',
        blocking: true,
        render(onSubmit) {
          return (
            <Card
              shadowSize={'lg'}
              borderRaduis={'3xl'}
              className="flex flex-col items-center justify-center px-4 py-6"
            >
              <div className="bg-tertiary flex h-28 w-28 justify-center overflow-hidden rounded-full">
                <img className={'mt-6'} src={momImage} alt="card" />
              </div>
              <Typography
                type="h3"
                weight="bold"
                className="mt-4"
                lineHeight="snug"
                text={'New client registered!'}
              />
              <Typography
                type="body"
                color="textMid"
                className="mt-4 text-center"
                lineHeight="snug"
                text={
                  totalInfantsForMonth > 0
                    ? `Great job ${user?.firstName}, you've registered ${totalInfantsForMonth} children this month.`
                    : `Great job ${user?.firstName}, you've registered ${successMessage}`
                }
              />
              <Typography
                className="mt-7"
                color="textDark"
                text={`Keep going!`}
                type="body"
                lineHeight="snug"
              />
              <div className={'mt-4 flex w-full justify-center'}>
                <Button
                  text={`Close`}
                  icon={'XIcon'}
                  type={'filled'}
                  color={'primary'}
                  textColor={'white'}
                  className={'max-h-10 w-full'}
                  iconPosition={'start'}
                  onClick={() => {
                    history.push(ROUTES.CLIENTS.ROOT, {
                      activeTabIndex: HcwTabIndex.CLIENT,
                    });
                    onSubmit();
                  }}
                />
              </div>
            </Card>
          );
        },
      }),
    [dialog, history, totalInfantsForMonth, successMessage, user?.firstName]
  );

  const displayRecordEventDialog = useCallback(
    (child: InfantDto) => {
      dialog({
        blocking: false,
        position: DialogPosition.Middle,
        color: 'bg-white',
        render: (onClose) => {
          return (
            <ActionModal
              className="z-50"
              icon="ExclamationCircleIcon"
              iconColor="alertMain"
              title={`Would you like to record the event: ${mother?.user?.firstName}'s baby was born?`}
              detailText={`${child?.firstName}'s birth date is close to ${mother?.user?.firstName}'s expected delivery date. If ${mother?.user?.firstName} has given birth, record the event!`}
              actionButtons={[
                {
                  colour: 'primary',
                  text: 'Yes, record this event',
                  textColour: 'white',
                  type: 'filled',
                  leadingIcon: 'ClipboardCheckIcon',
                  onClick: () => {
                    history.push(
                      `${ROUTES.CLIENTS.MOM_PROFILE.ROOT}${mother?.user?.id}/record-event`,
                      {
                        isFromInfantForm: true,
                      }
                    );
                    onClose();
                  },
                },
                {
                  colour: 'primary',
                  text: 'No, close',
                  textColour: 'primary',
                  type: 'outlined',
                  leadingIcon: 'XIcon',
                  onClick: () => {
                    history.push(ROUTES.CLIENTS.ROOT);
                    onClose();
                  },
                },
              ]}
            />
          );
        },
      });
    },
    [dialog, history, mother?.user?.firstName, mother?.user?.id]
  );

  const getChildWithCloseBirthday = useCallback(() => {
    const babyAlreadyBorn =
      mother?.eventRecord?.eventRecordTypeName === 'baby_was_born';

    if (
      mother?.user &&
      isAlreadyClient &&
      !!mother?.expectedDateOfDelivery &&
      !babyAlreadyBorn
    ) {
      const { nextDate, previousDate } = getPreviousAndNextMonths(
        mother.expectedDateOfDelivery,
        2
      );

      const child = multipleChildrenArray.find((child) => {
        const childBirthday = child.dateOfBirth && new Date(child.dateOfBirth);

        return (
          childBirthday &&
          childBirthday >= previousDate &&
          childBirthday <= nextDate
        );
      });

      if (child) return child as unknown as InfantDto;

      if (infantDetails?.dateOfBirth) {
        const childBirthday = new Date(infantDetails.dateOfBirth);
        if (childBirthday >= previousDate && childBirthday <= nextDate) {
          return infantDetails as unknown as InfantDto;
        }
      }
    }
  }, [isAlreadyClient, mother, multipleChildrenArray, infantDetails]);

  useEffect(() => {
    if (activeStep === InfantRegisterSteps.motherDetails && isAlreadyClient) {
      setLabel('step 4 of 4');
    }

    if (activeStep === InfantRegisterSteps.motherDetails && !isAlreadyClient) {
      setLabel('step 4 of 6');
    }
  }, [activeStep, isAlreadyClient]);

  const onSuccess = useCallback(() => {
    if (isFulfilled) {
      if (registerChildEvent && registerChildEvent.id) {
        (async () =>
          await appDispatch(
            eventRecordThunkActions.updateEventRecordStatus({
              eventId: registerChildEvent.id!,
              status: false,
            })
          ).unwrap())();
      }

      if ((motherId || isInfantEvent) && !registerChildEvent) {
        const input = {
          eventRecordTypeId:
            bornEventId || location.state.recordEventInput?.eventRecordTypeId,
          ...(isInfantEvent ? { infantId } : { motherId }),
          notes: location.state.recordEventInput?.notes || notes || '',
          status: false,
        };

        (async () =>
          await appDispatch(
            eventRecordThunkActions.addEventRecord({
              input,
            })
          ).unwrap())();
        if (motherId) {
          appDispatch(motherActions.addEventRecordToMother(input));
        }
      } else {
        if (relationshipId === MOTHER_TYPE_ID) {
          const child = getChildWithCloseBirthday();

          if (!!child) {
            return displayRecordEventDialog(child);
          }
        }
      }

      showSuccessMessage();

      if (healthCareWorker) {
        (async () =>
          await appDispatch(
            caregiverThunkActions.getCaregiversForHealthCareWorker({})
          ).unwrap())();
      }
    } else {
      showSuccessMessage();
    }
  }, [
    isFulfilled,
    motherId,
    isInfantEvent,
    registerChildEvent,
    relationshipId,
    healthCareWorker,
    bornEventId,
    location.state.recordEventInput?.eventRecordTypeId,
    location.state.recordEventInput?.notes,
    infantId,
    appDispatch,
    getChildWithCloseBirthday,
    displayRecordEventDialog,
    showSuccessMessage,
  ]);

  useEffect(() => {
    if (!isLoading && wasLoading) {
      onSuccess();
    }
  }, [isLoading, onSuccess, wasLoading]);

  useEffect(() => {
    if (wasLoadingEventRecord && !isLoadingEventRecord) {
      if (isRejectedEventRecord) {
        errorDialog(
          `The child has been registered, but it was not possible to record the event. Please try to record the event again.`
        );
        return history.push(ROUTES.CLIENTS.ROOT);
      }
    }
  }, [
    errorDialog,
    history,
    isLoadingEventRecord,
    isRejectedEventRecord,
    mother?.user?.firstName,
    wasLoadingEventRecord,
  ]);

  const handleOnClose = useCallback(() => {
    dialog({
      blocking: false,
      position: DialogPosition.Middle,
      color: 'bg-white',
      render: (onClose) => {
        return (
          <ActionModal
            className="z-50"
            icon="ExclamationCircleIcon"
            iconColor="alertMain"
            iconClassName="h-10 w-10"
            title="Are you sure you want to exit?"
            detailText="If you exit now you will lose your progress."
            actionButtons={[
              {
                colour: 'primary',
                text: 'Exit',
                textColour: 'white',
                type: 'filled',
                leadingIcon: 'LoginIcon',
                onClick: () => {
                  history.push(ROUTES.CLIENTS.ROOT, {
                    activeTabIndex: HcwTabIndex.CLIENT,
                  });
                  onClose();
                },
              },
              {
                colour: 'primary',
                text: 'Continue editing',
                textColour: 'primary',
                type: 'outlined',
                leadingIcon: 'PencilIcon',
                onClick: onClose,
              },
            ]}
          />
        );
      },
    });
  }, [dialog, history]);

  const handleOnBack = useCallback(() => {
    switch (activeStep) {
      case InfantRegisterSteps.infantDetails:
        // For single child OR first child in multiple children
        if (
          !numberOfChildren ||
          numberOfChildren === 1 ||
          currentChildIndex === 0
        ) {
          setLabel(`step 1 of 6`);
          setActiveStep(InfantRegisterSteps.consentAgreement);
        } else {
          // Multiple children: go back to previous child's Road to Health step
          const previousChildIndex = currentChildIndex - 1;
          setCurrentChildIndex(previousChildIndex);
          setMultipleChildrenCount(multipleChildrenCount - 1);

          const previousChild = multipleChildrenArray[previousChildIndex];
          if (previousChild) {
            setInfantDetails({
              firstName: previousChild.firstName,
              dateOfBirth: previousChild.dateOfBirth,
              genderId: previousChild.genderId,
            });
            setInfantRoadToHealthBook({
              weightAtBirth: previousChild.weightAtBirth,
              lengthAtBirth: previousChild.lengthAtBirth,
              roadToHealthBook: previousChild.roadToHealthBook,
              notRoadToHealthBook: previousChild.notRoadToHealthBook,
            });
            setInfantVisits(previousChild.visits);
          }

          setLabel(`step 3 of 6`);
          setActiveStep(InfantRegisterSteps.infantRoadToHealth);
        }
        break;

      case InfantRegisterSteps.infantRoadToHealth:
        setLabel(`step 2 of 6`);
        setActiveStep(InfantRegisterSteps.infantDetails);
        // For multiple children, restore infant details from array if not in current state
        if (numberOfChildren && numberOfChildren > 1) {
          const existingChild = multipleChildrenArray[currentChildIndex];
          if (existingChild && !infantDetails) {
            setInfantDetails({
              firstName: existingChild.firstName,
              dateOfBirth: existingChild.dateOfBirth,
              genderId: existingChild.genderId,
            });
          }
        }
        break;

      case InfantRegisterSteps.motherDetails:
        setLabel(`step 3 of 6`);
        setActiveStep(InfantRegisterSteps.infantRoadToHealth);

        // For multiple children, restore last child's data
        if (numberOfChildren && numberOfChildren > 1) {
          const lastChildIndex = multipleChildrenArray.length - 1;
          if (lastChildIndex >= 0) {
            setCurrentChildIndex(lastChildIndex);
            setMultipleChildrenCount(multipleChildrenArray.length);

            const lastChild = multipleChildrenArray[lastChildIndex];
            setInfantRoadToHealthBook({
              weightAtBirth: lastChild.weightAtBirth,
              lengthAtBirth: lastChild.lengthAtBirth,
              roadToHealthBook: lastChild.roadToHealthBook,
              notRoadToHealthBook: lastChild.notRoadToHealthBook,
            });
            setInfantDetails({
              firstName: lastChild.firstName,
              dateOfBirth: lastChild.dateOfBirth,
              genderId: lastChild.genderId,
            });
            setInfantVisits(lastChild.visits);
          }
        }
        // For single child, data is already in infantDetails and infantRoadToHealthBook state
        break;

      case InfantRegisterSteps.pregnantContactInformation:
        setLabel(`step 4 of 6`);
        setActiveStep(InfantRegisterSteps.motherDetails);
        break;

      case InfantRegisterSteps.pregnantAddress:
        setLabel(`step 5 of 6`);
        setActiveStep(InfantRegisterSteps.pregnantContactInformation);
        break;

      case InfantRegisterSteps.consentAgreement:
        handleOnClose();
        break;

      default:
        setActiveStep(InfantRegisterSteps.consentAgreement);
        break;
    }
    return steps(activeStep);
  }, [
    activeStep,
    currentChildIndex,
    handleOnClose,
    multipleChildrenArray,
    multipleChildrenCount,
    numberOfChildren,
    steps,
    infantDetails,
    infantRoadToHealthBook,
  ]);

  return (
    <div className="text-textMid">
      <BannerWrapper
        size={'normal'}
        renderBorder={true}
        title={'Child registration'}
        subTitle={label}
        onClose={handleOnClose}
        onBack={handleOnBack}
        displayOffline={!isOnline}
      ></BannerWrapper>
      <div
        className={'flex flex-col overflow-auto px-4 pb-5'}
        style={{ height: height - BANNER_HEIGHT }}
      >
        {steps(activeStep)}
      </div>
    </div>
  );
};
